<template>
    <main-dialog
        :show="showDialog"
        :title="$t('creditTopup.manualTitle')"
        :subtitle="$t('creditTopup.manualSubtitle')"
        max-width="480px"
        @close="close"
    >
        <div class="manual-topup-presets mb-3">
            <b-button
                v-for="preset in presets"
                :key="preset"
                :variant="manualTopupAmount === preset && !manualTopupCustom ? 'primary' : 'outline-secondary'"
                size="sm"
                class="mr-2 mb-2"
                @click="selectPreset(preset)"
            >
                {{ formatPreset(preset) }}
            </b-button>
            <b-button
                :variant="manualTopupCustom ? 'primary' : 'outline-secondary'"
                size="sm"
                class="mr-2 mb-2"
                @click="manualTopupCustom = true; manualTopupAmount = 0"
            >
                {{ $t('creditTopup.manualCustom') }}
            </b-button>
        </div>

        <div class="form-group" v-if="manualTopupCustom">
            <label>{{ $t('creditTopup.manualAmountLabel') }} ({{ currencyCode }})</label>
            <b-input-group :append="currencyCode">
                <b-form-input
                    v-model.number="manualTopupCustomAmount"
                    type="number"
                    min="1"
                    step="1"
                    :placeholder="$t('creditTopup.manualCustomPlaceholder')"
                    autofocus
                ></b-form-input>
            </b-input-group>
        </div>

        <div v-if="effectiveAmount > 0" class="alert alert-success py-2 mb-3" style="font-size:0.85rem">
            {{ $t('creditTopup.manualAddingSummary', { pay: formattedPay, receive: formattedReceive, bonus: topupBonus }) }}
        </div>

        <template #footer>
            <div class="d-flex justify-content-between w-100">
                <b-button variant="link" size="sm" class="px-0 text-muted" @click="goToSettings">
                    {{ $t('creditTopup.headerManageLink') }}
                </b-button>
                <div>
                    <b-button variant="outline-secondary" size="sm" class="mr-2" @click="close">
                        {{ $t('cancel') }}
                    </b-button>
                    <b-button
                        variant="success"
                        size="sm"
                        @click="doTopup"
                        :disabled="loading || effectiveAmount <= 0"
                    >
                        <b-spinner v-if="loading" small class="mr-1" />
                        {{ $t('creditTopup.manualConfirm') }}
                    </b-button>
                </div>
            </div>
        </template>
    </main-dialog>
</template>

<script>
export default {
    name: 'CreditTopupDialog',

    data: function() {
        return {
            showDialog: false,
            presets: [5, 10, 25, 50, 100],
            manualTopupAmount: 0,
            manualTopupCustom: false,
            manualTopupCustomAmount: null,
            currency: 'USD',
            exchangeRate: 1,
            loading: false,
            topupBonus: 10
        }
    },

    computed: {
        currencyCode: function() {
            return this.currency === 'USD' ? 'USD' : this.currency
        },
        effectiveAmount: function() {
            if (this.manualTopupCustom) {
                return this.manualTopupCustomAmount > 0 ? this.manualTopupCustomAmount : 0
            }
            // Presets are in USD — convert to billing currency
            var base = this.manualTopupAmount > 0 ? this.manualTopupAmount : 0
            return this.exchangeRate !== 1 ? base * this.exchangeRate : base
        },
        receivedAmount: function() {
            if (!this.effectiveAmount) return 0
            return this.effectiveAmount * (1 + this.topupBonus / 100)
        },
        formattedPay: function() {
            return this.formatMoney(this.effectiveAmount)
        },
        formattedReceive: function() {
            return this.formatMoney(this.receivedAmount)
        }
    },


    mounted: function() {
        var self = this
        this.$root.$on('open-credit-topup-dialog', function(options) {
            self.open(options)
        })
    },

    beforeDestroy: function() {
        this.$root.$off('open-credit-topup-dialog')
    },

    methods: {
        open: function(options) {
            this.manualTopupAmount = 0
            this.manualTopupCustom = false
            this.manualTopupCustomAmount = null
            this.loading = false
            this.currency = (options && options.currency) ? options.currency : 'USD'
            this.exchangeRate = (options && options.exchangeRate && options.exchangeRate > 0) ? options.exchangeRate : 1
            if (options && options.topupBonus != null) {
                this.topupBonus = options.topupBonus
            }
            this.showDialog = true
        },

        close: function() {
            this.showDialog = false
        },

        formatPreset: function(preset) {
            // Presets are in USD — convert to billing currency for display
            var display = this.exchangeRate !== 1 ? preset * this.exchangeRate : preset
            return this.formatMoney(display)
        },

        formatMoney: function(amount) {
            // Amount is already in the billing currency — just format
            if (this.currency === 'USD') {
                return '$' + amount.toFixed(2)
            }
            return amount.toFixed(2) + ' ' + this.currency
        },

        selectPreset: function(preset) {
            this.manualTopupAmount = preset
            this.manualTopupCustom = false
        },

        goToSettings: function() {
            this.close()
            this.$router.push('/admin/projects/settings?subtab=subscriptions#account')
        },

        doTopup: function() {
            // Compute USD cents: presets are already in USD, custom amounts are in billing currency
            var usdCents
            if (this.manualTopupCustom) {
                usdCents = Math.round(this.manualTopupCustomAmount / this.exchangeRate * 100)
            } else {
                usdCents = this.manualTopupAmount * 100
            }
            if (usdCents <= 0) return

            var self = this
            self.loading = true

            self.$transactionApi.post('/billing/topup', {
                amount_cents: usdCents,
                currency: self.currency
            }).then(function(resp) {
                if (resp && resp.data && resp.data.checkout_url) {
                    window.location.href = resp.data.checkout_url
                }
            }).catch(function(err) {
                var msg = err && err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : self.$t('creditTopup.manualError')
                self.$bvToast.toast(msg, { variant: 'danger', solid: true })
            }).finally(function() {
                self.loading = false
            })
        }
    }
}
</script>
